import { BaseDialog } from '@/components/BaseDialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SingleQRCard } from '@/features/qrcode/components/SingleQRCard'
import { getScanUrl } from '@/features/qrcode/utils/qrcode'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useTranslation } from 'react-i18next'
import { useGetPatientItems } from '../hooks/usePatient'
import { Patient } from '../types/patient'
import { PatientCard } from './PatientCard'
import { PatientDetailContent } from './PatientDetailContent'

export function PatientDetailDialog({
  open,
  onOpenChange,
  patientId,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  patientId?: string | null
}) {
  const { t } = useTranslation()
  const { formatDateTime } = useDateFormatter()
  const { data: patientItems, isFetching: isFetchingPatientItems } = useGetPatientItems(patientId!, !!patientId)

  if (!patientItems) return null

  const patient = patientItems?.patient
  const isLoading = isFetchingPatientItems

  const patientFields = [
    // 🟦 Basic Info
    { label: t('patient.hn'), value: (p: Patient) => p.hn || '-' },
    { label: t('patient.cardId'), value: (p: Patient) => p.cardId || '-' },
    { label: t('patient.fullName'), value: (p: Patient) => `${p.title}${p.firstName} ${p.lastName}` },
    { label: t('patient.midName'), value: (p: Patient) => p.midName || '-' },
    { label: t('patient.sex'), value: (p: Patient) => (p.sex === 'M' ? 'ชาย' : p.sex === 'F' ? 'หญิง' : '-') },
    {
      label: t('patient.birthdate'),
      value: (p: Patient) =>
        formatDateTime(p.birthDate, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }) || '-',
    },

    // 🟩 Medical Info
    { label: t('patient.bloodGroup'), value: (p: Patient) => p.bloodGroup || '-' },
    { label: t('patient.historyMedicine'), value: (p: Patient) => p.historyMedicine || '-' },

    // 🟨 Personal Info
    { label: t('patient.nationality'), value: (p: Patient) => p.nationality || '-' },
    { label: t('patient.race'), value: (p: Patient) => p.race || '-' },
    { label: t('patient.fullName'), value: (p: Patient) => p.religion || '-' },
    { label: t('patient.job'), value: (p: Patient) => p.job || '-' },

    // 🟧 Contact
    { label: t('patient.phoneNumber'), value: (p: Patient) => p.phoneNumber || '-' },
    { label: t('patient.email'), value: (p: Patient) => p.email || '-' },
    { label: t('patient.address'), value: (p: Patient) => p.address || '-' },

    // 🟥 Emergency Contact
    { label: t('patient.emergencyName'), value: (p: Patient) => p.emergencyName || '-' },
    { label: t('patient.emergencyPhoneNumber'), value: (p: Patient) => p.emergencyPhoneNumber || '-' },
    { label: t('patient.emergencyRelation'), value: (p: Patient) => p.emergencyRelation || '-' },

    // 🟫 Other
    { label: t('patient.passport'), value: (p: Patient) => p.passport || '-' },
    { label: t('patient.note'), value: (p: Patient) => p.note || '-' },
  ]

  return (
    <BaseDialog open={open} onOpenChange={onOpenChange} title={t('common.patient')} contentTestId="patient-detail-dialog">
      <Tabs defaultValue="info" className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="info" className="flex-1 text-xs" disabled={isLoading} data-testid="patient-detail-tab-info">
            {t('common.info')}
          </TabsTrigger>
          <TabsTrigger value="management" className="flex-1 text-xs" disabled={isLoading} data-testid="patient-detail-tab-management">
            {t('common.management')}
          </TabsTrigger>
          <TabsTrigger value="qrcode" className="flex-1 text-xs" disabled={isLoading} data-testid="patient-detail-tab-qrcode">
            {t('common.qrcode')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="info" className="mt-4">
          {isLoading ? (
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-border/30 bg-secondary/30 p-3">
              {Array.from({ length: patientFields.length }).map((_, i) => (
                <div key={i} className="flex flex-col gap-1">
                  <Skeleton className="h-3 w-20"></Skeleton>
                  <Skeleton className="h-4 w-32"></Skeleton>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3 rounded-lg border border-border/30 bg-secondary/30 p-3">
                  {patientFields.map((field, i) => (
                    <div key={i} className="flex flex-col gap-0.5">
                      <span className="text-[10px] text-muted-foreground">{field.label}</span>
                      <span className="text-xs font-medium text-foreground">{field.value(patient)}</span>
                    </div>
                  ))}
                </div>
              </div> */}
              <PatientCard patient={patient} variant="full" isLoading={isFetchingPatientItems}></PatientCard>
            </>
          )}
        </TabsContent>
        <TabsContent value="management" className="mt-4">
          <PatientDetailContent patientItems={patientItems}></PatientDetailContent>
        </TabsContent>
        <TabsContent value="qrcode" className="mt-4">
          <SingleQRCard
            title={patient?.fullName}
            subtitle={patient.hn}
            url={getScanUrl('patient', patient.id)}
            onPrint={() => {}}
          ></SingleQRCard>
        </TabsContent>
      </Tabs>
    </BaseDialog>
  )
}
